import { useState, useEffect, useRef, useCallback } from 'react';
import { postsAPI, uploadAPI, draftsAPI, scheduledAPI } from '../utils/api';
import { applyFilterToCanvas } from '../components/create-post/PhotoFilters';
import { toast } from 'sonner';

export const useCreatePost = ({ isVideo, onPostCreated, onClose }) => {
  const [postType, setPostType] = useState(isVideo ? 'video' : 'post');
  const [content, setContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollDuration, setPollDuration] = useState('1day');
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselPreviews, setCarouselPreviews] = useState([]);
  const [threadPosts, setThreadPosts] = useState([{ content: '', media: null, mediaPreview: null }]);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftsExpanded, setDraftsExpanded] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);
  const [isExclusive, setIsExclusive] = useState(false);
  const autoSaveTimer = useRef(null);
  const isLoadingDraft = useRef(false);

  const postLabel = postType === 'poll' ? 'Poll' : postType === 'carousel' ? 'Carousel' : postType === 'thread' ? 'Talk' : postType === 'audio' ? 'Audio' : postType === 'link' ? 'Link' : isVideo ? 'Video' : 'Point';

  // --- Drafts ---
  const loadDrafts = async () => {
    try { const res = await draftsAPI.getAll(); setDrafts(res.data); } catch (e) { console.error("Failed to load drafts:", e); }
  };

  useEffect(() => { loadDrafts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildDraftPayload = useCallback(() => ({
    draft_id: activeDraftId, post_type: postType, content,
    poll_question: pollQuestion, poll_options: pollOptions, poll_duration: pollDuration,
    thread_posts: threadPosts.map(p => ({ content: p.content })),
    platforms: selectedPlatforms, music: selectedMusic,
  }), [activeDraftId, postType, content, pollQuestion, pollOptions, pollDuration, threadPosts, selectedPlatforms, selectedMusic]);

  const hasContent = useCallback(() => {
    if (postType === 'poll') return pollQuestion.trim().length > 0;
    if (postType === 'thread') return threadPosts.some(p => p.content.trim().length > 0);
    if (postType === 'audio') return mediaFile !== null || content.trim().length > 0;
    if (postType === 'link') return linkPreview !== null || content.trim().length > 0;
    return content.trim().length > 0;
  }, [postType, content, pollQuestion, threadPosts, mediaFile, linkPreview]);

  useEffect(() => {
    if (isLoadingDraft.current || !hasContent()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      try {
        setDraftStatus('saving');
        const res = await draftsAPI.save(buildDraftPayload());
        if (!activeDraftId && res.data.id) setActiveDraftId(res.data.id);
        setDraftStatus('saved');
        loadDrafts();
        setTimeout(() => setDraftStatus(null), 2000);
      } catch { setDraftStatus(null); }
    }, 3000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [content, pollQuestion, pollOptions, pollDuration, threadPosts, selectedPlatforms, selectedMusic, postType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDraft = (draft) => {
    isLoadingDraft.current = true;
    setActiveDraftId(draft.id);
    setPostType(draft.post_type || 'post');
    setContent(draft.content || '');
    setPollQuestion(draft.poll_question || '');
    setPollOptions(draft.poll_options?.length >= 2 ? draft.poll_options : ['', '']);
    setPollDuration(draft.poll_duration || '1day');
    setThreadPosts(draft.thread_posts?.length > 0 ? draft.thread_posts.map(p => ({ content: p.content || '', media: null, mediaPreview: null })) : [{ content: '', media: null, mediaPreview: null }]);
    setSelectedPlatforms(draft.platforms || []);
    setSelectedMusic(draft.music || null);
    setDraftsExpanded(false);
    toast.success('Draft loaded');
    setTimeout(() => { isLoadingDraft.current = false; }, 500);
  };

  const deleteDraft = async (draftId) => {
    try { await draftsAPI.delete(draftId); setDrafts(prev => prev.filter(d => d.id !== draftId)); if (activeDraftId === draftId) setActiveDraftId(null); toast.success('Draft deleted'); }
    catch { toast.error('Failed to delete draft'); }
  };

  // --- Media ---
  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) { setMediaFile(file); const r = new FileReader(); r.onloadend = () => setMediaPreview(r.result); r.readAsDataURL(file); }
  };
  const clearMedia = () => { setMediaFile(null); setMediaPreview(null); };

  const handleCarouselImageAdd = (e) => {
    const files = Array.from(e.target.files);
    if (carouselImages.length + files.length > 10) { toast.error('Maximum 10 images allowed'); return; }
    const newImages = [...carouselImages];
    const newPreviews = [...carouselPreviews];
    files.forEach(file => { newImages.push(file); const r = new FileReader(); r.onloadend = () => { newPreviews.push(r.result); setCarouselPreviews([...newPreviews]); }; r.readAsDataURL(file); });
    setCarouselImages(newImages);
  };
  const removeCarouselImage = (i) => { setCarouselImages(carouselImages.filter((_, j) => j !== i)); setCarouselPreviews(carouselPreviews.filter((_, j) => j !== i)); };

  // --- Poll ---
  const addPollOption = () => { if (pollOptions.length < 4) setPollOptions([...pollOptions, '']); };
  const removePollOption = (i) => { if (pollOptions.length > 2) setPollOptions(pollOptions.filter((_, j) => j !== i)); };
  const updatePollOption = (i, v) => { const n = [...pollOptions]; n[i] = v; setPollOptions(n); };

  // --- Thread ---
  const addThreadPost = () => { if (threadPosts.length < 10) setThreadPosts([...threadPosts, { content: '', media: null, mediaPreview: null }]); };
  const removeThreadPost = (i) => { if (threadPosts.length > 1) setThreadPosts(threadPosts.filter((_, j) => j !== i)); };
  const updateThreadPost = (i, field, v) => { const n = [...threadPosts]; n[i][field] = v; setThreadPosts(n); };
  const handleThreadMediaChange = (i, e) => {
    const file = e.target.files[0];
    if (file) { const r = new FileReader(); r.onloadend = () => { const n = [...threadPosts]; n[i].media = file; n[i].mediaPreview = r.result; setThreadPosts(n); }; r.readAsDataURL(file); }
  };

  // --- Platforms ---
  const togglePlatform = (id) => setSelectedPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  const selectAllPlatforms = (allPlatforms) => setSelectedPlatforms(prev => prev.length === allPlatforms.length ? [] : allPlatforms.map(p => p.id));

  // --- Filter ---
  const handleFilterChange = useCallback((filter) => { setSelectedFilter(filter); }, []);

  const getFilteredMediaFile = async () => {
    if (!mediaFile || !selectedFilter || selectedFilter.id === 'normal' || !mediaPreview) return mediaFile;
    if (mediaFile.type?.startsWith('video')) return mediaFile;
    const blob = await applyFilterToCanvas(mediaPreview, selectedFilter.css);
    if (!blob) return mediaFile;
    return new File([blob], mediaFile.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
  };

  // --- Validation helpers ---
  const validatePost = () => {
    if (postType === 'poll') {
      if (!pollQuestion.trim()) { toast.error('Please enter a poll question'); return null; }
      const valid = pollOptions.filter(o => o.trim());
      if (valid.length < 2) { toast.error('Please add at least 2 poll options'); return null; }
      return pollQuestion;
    }
    if (postType === 'carousel') {
      if (!isScheduled && carouselImages.length < 2) { toast.error('Please add at least 2 images for a carousel'); return null; }
      return content || 'Photo carousel';
    }
    if (postType === 'thread') {
      const valid = threadPosts.filter(p => p.content.trim());
      if (valid.length < 2) { toast.error('Please add at least 2 points for a talk'); return null; }
      return valid[0].content;
    }
    if (postType === 'audio') {
      if (!mediaFile) { toast.error('Please upload an audio file'); return null; }
      return content || 'Audio post';
    }
    if (postType === 'link') {
      if (!linkPreview) { toast.error('Please add a link'); return null; }
      return content || linkPreview.title || linkPreview.url;
    }
    if (!content.trim()) { toast.error('Please enter some content'); return null; }
    return content;
  };

  const submitScheduled = async (postContent) => {
    if (!scheduledDate || !scheduledTime) { toast.error('Please pick a date and time'); return false; }
    const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    if (new Date(scheduledAt) <= new Date()) { toast.error('Scheduled time must be in the future'); return false; }
    await scheduledAPI.create({ content: postContent, platforms: selectedPlatforms, post_type: postType, poll_options: postType === 'poll' ? pollOptions.filter(o => o.trim()) : [], music: selectedMusic || null, scheduled_at: scheduledAt });
    toast.success(`${postLabel} scheduled!`);
    return true;
  };

  const submitImmediate = async () => {
    if (postType === 'poll') {
      await postsAPI.create({ content: pollQuestion, platforms: selectedPlatforms, post_type: 'poll', poll_options: pollOptions.filter(o => o.trim()), is_exclusive: isExclusive });
    } else if (postType === 'carousel') {
      const urls = []; for (const img of carouselImages) { const r = await uploadAPI.uploadMedia(img); urls.push(r.data.url); }
      await postsAPI.create({ content: content || 'Photo carousel', platforms: selectedPlatforms, post_type: 'carousel', media_urls: urls, is_exclusive: isExclusive });
    } else if (postType === 'thread') {
      const valid = threadPosts.filter(p => p.content.trim()); let parentId = null;
      for (const post of valid) {
        let mUrl = null, mType = null;
        if (post.media) { const r = await uploadAPI.uploadMedia(post.media); mUrl = r.data.url; mType = r.data.type; }
        const res = await postsAPI.create({ content: post.content, platforms: selectedPlatforms, media_url: mUrl, media_type: mType, post_type: 'thread', thread_parent_id: parentId, is_exclusive: isExclusive });
        if (!parentId) parentId = res.data.id;
      }
    } else {
      let mUrl = null, mType = null;
      if (mediaFile) {
        const fileToUpload = await getFilteredMediaFile();
        const r = await uploadAPI.uploadMedia(fileToUpload);
        mUrl = r.data.url; mType = r.data.type;
      }
      if (postType === 'audio') await postsAPI.create({ content: content || 'Audio post', platforms: selectedPlatforms, media_url: mUrl, media_type: 'audio', post_type: 'audio', is_exclusive: isExclusive });
      else if (postType === 'link') await postsAPI.create({ content: content || linkPreview.title || linkPreview.url, platforms: selectedPlatforms, post_type: 'link', link_preview: linkPreview, is_exclusive: isExclusive });
      else await postsAPI.create({ content, platforms: selectedPlatforms, media_url: mUrl, media_type: mType, post_type: isVideo ? 'video' : 'post', music: selectedMusic || undefined, is_exclusive: isExclusive });
    }
    toast.success(`${postLabel} created!`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    const postContent = validatePost();
    if (postContent === null) return;
    setLoading(true);
    try {
      if (isScheduled) { if (!(await submitScheduled(postContent))) { setLoading(false); return; } }
      else { await submitImmediate(); }
      if (activeDraftId) { try { await draftsAPI.delete(activeDraftId); } catch (e) { console.error("Failed to delete draft:", e); } }
      onPostCreated();
      onClose();
    } catch {
      toast.error('Failed to create post');
    } finally { setLoading(false); }
  };

  return {
    // State
    postType, setPostType, content, setContent, selectedPlatforms, mediaFile, mediaPreview,
    loading, showMusicPicker, setShowMusicPicker, selectedMusic, setSelectedMusic,
    pollQuestion, setPollQuestion, pollOptions, pollDuration, setPollDuration,
    carouselPreviews, threadPosts, linkPreview, setLinkPreview,
    isScheduled, setIsScheduled, scheduledDate, setScheduledDate, scheduledTime, setScheduledTime,
    drafts, draftsExpanded, setDraftsExpanded, draftStatus, isExclusive, setIsExclusive, postLabel,
    // Actions
    handleMediaChange, clearMedia, handleCarouselImageAdd, removeCarouselImage,
    addPollOption, removePollOption, updatePollOption,
    addThreadPost, removeThreadPost, updateThreadPost, handleThreadMediaChange,
    togglePlatform, selectAllPlatforms, loadDraft, deleteDraft, handleSubmit, handleFilterChange,
  };
};
